/* WHAT THE CHART CACHE IS DOING, on both sides of it.
 *
 * ═══ THERE ARE TWO CACHES AND THEY ARE NOT THE SAME THING ═══
 *
 * THIS DEVICE — `tilestore.ts`, IndexedDB, one per browser. The `gdtile://`
 * protocol checks it before issuing any request, so a tile this iPad already
 * holds costs no HTTP at all. It is capped (MAX_TILES) and evicted; Safari will
 * throw it away after about a week unused unless persistence was granted.
 * Every device aboard has its own, and they do not share.
 *
 * THE BOAT — `ops/chart-cache`, SQLite on the pi, shared by every device on the
 * network. It fetches from CHS once, compacts to lossless WebP in the
 * background (measured 59% smaller), and de-duplicates identical bodies — which
 * matters more than it sounds, because every square outside CHS coverage comes
 * back as the same 363-byte transparent PNG. It keeps tiles for ever; the
 * plugin's own admin page owns the heavy operations (seeding an area, purging,
 * compacting).
 *
 * A tile therefore has three chances to avoid the internet: this device's
 * IndexedDB, the pi's SQLite, then CHS. This module reports the first two.
 *
 * ═══ WHY THE SERVER HALF IS ALLOWED TO BE ABSENT ═══
 *
 * The hosted chart has no pi. That is not a degraded boat — it is a different
 * host, and it says so rather than showing zeroes that look like a broken
 * cache. Same rule as every layer that declares `requires`.
 */

import { sources } from '../sources.ts';
import { tileCacheStats } from './tile-protocol.ts';
import * as tilestore from './tilestore.ts';

/** What this device is holding, and what it has done this session. */
export interface DeviceCache {
  /** Tiles in IndexedDB. */
  tiles: number;
  /** Bytes this origin is using, and is allowed — the browser's own figures. */
  usage: number;
  quota: number;
  /** Since the page loaded. NOT persisted: they are about this session's
   *  behaviour, and a lifetime counter would hide a cache that has stopped
   *  working today. */
  hit: number;
  miss: number;
  stored: number;
  failed: number;
}

/** What the pi's cache reports. Mirrors the plugin's `/stats` route. */
export interface ServerCache {
  generation: number;
  /** Squares known: charted + blank. */
  tiles: number;
  /** Squares with a real chart on them. */
  charted: number;
  /** Squares CHS says have no chart. Never re-fetched — see the plugin README. */
  blanks: number;
  /** The SQLite file on disk. */
  bytes: number;
  /** How much the WebP compaction has saved, as a percentage. */
  savedPct: number;
  /** The pi's own hit rate, across every device aboard. */
  hitRate: number;
  /** The upstream breaker: open means it has stopped trying to reach CHS. */
  upstream: unknown;
  seeding: unknown;
}

const rate = (hit: number, miss: number) => (hit + miss === 0 ? 0 : hit / (hit + miss));

class CacheStats {
  device = $state<DeviceCache | null>(null);
  server = $state<ServerCache | null>(null);
  /** Why there is no server figure: absent provider, or the reason it failed. */
  serverError = $state<string | null>(null);
  loading = $state(false);

  /** Hit rate on THIS device this session, 0..1. */
  get deviceHitRate(): number {
    const d = this.device;
    return d ? rate(d.hit, d.miss) : 0;
  }

  /** True when this host has a pi cache to ask about at all. */
  get hasServer(): boolean {
    return sources().has('chart-cache');
  }

  async refresh(): Promise<void> {
    this.loading = true;
    try {
      /* Both halves independently: a browser that refuses IndexedDB must not
       * hide the boat's figures, and a pi that is asleep must not hide this
       * device's. */
      await Promise.all([this.#refreshDevice(), this.#refreshServer()]);
    } finally {
      this.loading = false;
    }
  }

  async #refreshDevice(): Promise<void> {
    try {
      const s = await tilestore.stats();
      this.device = { ...s, ...tileCacheStats };
    } catch {
      /* Private browsing, a blocked origin, a quota refusal — none of which is
       * worth an error banner. No figure is the honest display. */
      this.device = null;
    }
  }

  async #refreshServer(): Promise<void> {
    if (!this.hasServer) {
      this.server = null;
      this.serverError = 'no chart-cache provider on this host';
      return;
    }
    try {
      const r = await sources().fetch('chart-cache', 'stats', {
        signal: AbortSignal.timeout(6000)
      });
      if (!r.ok) {
        this.server = null;
        this.serverError = `the cache answered HTTP ${r.status}`;
        return;
      }
      this.server = (await r.json()) as ServerCache;
      this.serverError = null;
    } catch (e) {
      this.server = null;
      /* An unreachable pi is the normal condition of a boat at anchor with the
       * radio off, so this reads as a fact rather than a fault. */
      this.serverError = e instanceof Error && e.name === 'TimeoutError'
        ? 'the cache did not answer in time'
        : 'the cache is unreachable';
    }
  }

  /** Throw this device's tiles away. The boat's cache is untouched — the
   *  plugin's own admin page owns that, and it is shared by every device. */
  async clearDevice(): Promise<void> {
    await tilestore.clear();
    await this.#refreshDevice();
  }
}

export const cacheStats = new CacheStats();
